import React from "react";
import { View, Text, Pressable, StyleSheet, useWindowDimensions } from "react-native";
import { LastPageIcon, NextPageIcon } from "./PaginationIcons";
import Colors from "../../Styles/Shared/Colors";
import { PaginatorStyles } from "../../Styles/PaginatorStyles";
import DynamicSelect from '../DynamicControls/DynamicSelect';
import { DynamicSelectItem } from '../../Types/ControlTypes';

type PaginationControlsProps = {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    pageSizeOptions: number[];
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    onPageChange: (page: number) => void;
};

const PaginationControls = ({
    currentPage,
    totalPages,
    totalRecords,
    pageSizeOptions,
    pageSize,
    setPageSize,
    onPageChange,
}: PaginationControlsProps) => {
    const { width } = useWindowDimensions();
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalRecords);

    const renderPageNumbers = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push("...");
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push("...");
            pages.push(totalPages);
        }

        return pages.map((p, idx) =>
            typeof p === "string" ? (
                <Text key={`ellipsis-${idx}`} style={PaginatorStyles.ellipsis}>
                    {p}
                </Text>
            ) : (
                <Pressable 
                    key={`page-${p}`} 
                    onPress={() => onPageChange(p)}
                     style={[PaginatorStyles.pageButton]}
                >
                    <Text style={[PaginatorStyles.pageText, p === currentPage && PaginatorStyles.activePageText]}>
                        {p}
                    </Text>
                </Pressable>
            )
        );
    };
    const selectValues: DynamicSelectItem[] = [];
    pageSizeOptions.forEach(element => {
        selectValues.push({label: element.toString(), value: element.toString()});
    });

    const isSmall = width <= 1110;
    return (
        <View style={PaginatorStyles.wrapper}>
            <View style={PaginatorStyles.paginationInfoDiv}>
                <View style={{flex: isSmall ? 0 : 1}}/>
                <Text style={PaginatorStyles.summary}>{`Showing ${start}-${end} of ${totalRecords} Records`}</Text>
                <View style={{flex: 1, alignItems:  "flex-end"}}>
                    <DynamicSelect
                        value={pageSize.toString()}
                        label='Show Per Page:'
                        config={{
                            options: selectValues,
                        }}
                        disableDefaultOption={true}
                        onChange={(_c, _a, value) => setPageSize(Number.parseInt(value))}
                        labelFlexDirection='row'
                    />

                </View>
            </View>
            <View style={PaginatorStyles.paginationBar}>
                <Pressable
                    onPress={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    style={[PaginatorStyles.navButton, currentPage === 1 && PaginatorStyles.disabled]}
                >
                    <Text style={PaginatorStyles.navText}>
                        <LastPageIcon
                            rotation={180}
                            translate={1}
                            color={currentPage === 1 ? Colors.gray : Colors.white}
                        />
                    </Text>
                </Pressable>
                <Pressable
                    onPress={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={[PaginatorStyles.navButton, currentPage === 1 && PaginatorStyles.disabled]}
                >
                    <Text style={PaginatorStyles.navText}>
                        <NextPageIcon
                            rotation={180}
                            translate={1}
                            color={currentPage === 1 ? Colors.gray : Colors.white}
                        />
                    </Text>
                </Pressable>

                <View style={PaginatorStyles.pageNumberContainer}>{renderPageNumbers()}</View>

                <Pressable
                    onPress={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={[PaginatorStyles.navButton, currentPage === totalPages && PaginatorStyles.disabled]}
                >
                    <Text style={PaginatorStyles.navText}>
                        <NextPageIcon
                         color={currentPage === totalPages ? Colors.gray : Colors.white}
                         />
                    </Text>
                </Pressable>
                <Pressable
                    onPress={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    style={[PaginatorStyles.navButton, currentPage === totalPages && PaginatorStyles.disabled]}
                >
                    <Text style={PaginatorStyles.navText}>
                        <LastPageIcon
                         color={currentPage === totalPages ? Colors.gray : Colors.white}
                        />
                    </Text>
                </Pressable>
            </View>
        </View>
    );
};

export default PaginationControls;
